<template>
  <div>
    <one-data-column>
      <data-label slot="label">ID</data-label>
      <span class="t-payment-id">{{ payment.id }}</span>
    </one-data-column>

    <one-data-column v-if="payment.applicationPaymentId != null">
      <data-label slot="label">Application payment ID</data-label>
      <span class="t-application-payment-id">{{ payment.applicationPaymentId }}</span>
    </one-data-column>

    <one-data-column v-if="payment.bilderAccountPaymentId != null">
      <data-label slot="label">Internal payment ID</data-label>
      <router-link
        :to="createPaymentViewRoute(payment.bilderAccountPaymentId)"
        class="t-internal-payment-id"
      >{{ payment.bilderAccountPaymentId }}</router-link>
    </one-data-column>

    <one-data-column>
      <data-label slot="label">Amount</data-label>
      <span class="t-payment-amount">{{ payment.amount | number }} {{ payment.currency }}</span>
    </one-data-column>

    <one-data-column>
      <data-label slot="label">Description</data-label>
      <span
        class="t-payment-description"
        style="word-break: break-word"
      >{{ payment.description }}</span>
    </one-data-column>

    <one-data-column>
      <data-label slot="label">Created at</data-label>
      <span class="t-payment-created">{{ payment.createdAt | date }} {{ payment.createdAt | date('time') }}</span>
    </one-data-column>

    <one-data-column>
      <data-label slot="label">Status</data-label>
      <open-banking-payment-status-label :status="payment.status" />
    </one-data-column>

    <one-data-column>
      <data-heading
        slot="label"
        heading="Sender"
        spaced
      />
    </one-data-column>

    <one-data-column>
      <data-label slot="label">Customer ID</data-label>
      <span class="t-application-customer-id">{{ payment.applicationCustomerId }}</span>
    </one-data-column>

    <template v-if="payment.sender != null">
      <one-data-column>
        <data-label slot="label">Name</data-label>
        <span class="t-sender-name">{{ payment.sender.name ?? 'Unknown' }}</span>
      </one-data-column>

      <one-data-column>
        <data-label slot="label">Country</data-label>
        <span class="t-sender-country">{{ payment.sender.country != null ? getCountryName(payment.sender.country) : 'Unknown' }}</span>
      </one-data-column>

      <one-data-column>
        <data-label slot="label">Account number</data-label>
        <span class="t-sender-account-number">{{ payment.sender.accountNumber ?? 'Unknown' }}</span>
      </one-data-column>

      <one-data-column>
        <data-label slot="label">Email</data-label>
        <span class="t-sender-email">{{ payment.sender.email ?? 'Unknown' }}</span>
      </one-data-column>

      <one-data-column v-if="payment.sender.sortCode != null">
        <data-label slot="label">Sort code</data-label>
        <span class="t-sender-sort-code">{{ payment.sender.sortCode }}</span>
      </one-data-column>
    </template>

    <one-data-column>
      <data-heading
        slot="label"
        heading="Recipient"
        spaced
      />
    </one-data-column>

    <one-data-column>
      <data-label slot="label">Name</data-label>
      <span class="t-recipient-name">{{ payment.recipient.name }}</span>
    </one-data-column>

    <one-data-column>
      <data-label slot="label">Country</data-label>
      <span class="t-recipient-country">{{ getCountryName(payment.recipient.country) }}</span>
    </one-data-column>

    <one-data-column>
      <data-label slot="label">Account number</data-label>
      <span class="t-recipient-account-number">{{ payment.recipient.accountNumber | iban }}</span>
    </one-data-column>

    <one-data-column v-if="payment.recipient.sortCode">
      <data-label slot="label">Sort code</data-label>
      <span class="t-recipient-sort-code">{{ payment.recipient.sortCode }}</span>
    </one-data-column>

    <template v-if="payment.institutionId != null">
      <one-data-column>
        <data-heading
          slot="label"
          heading="Institution"
          spaced
        />
      </one-data-column>

      <one-data-column>
        <data-label slot="label">ID</data-label>
        <span class="t-institution-id">{{ payment.institutionId }}</span>
      </one-data-column>

      <one-data-column>
        <data-label slot="label">Client ID</data-label>
        <span class="t-institution-client-id">{{ payment.institutionClientId }}</span>
      </one-data-column>

      <one-data-column>
        <data-label slot="label">Payment status</data-label>
        <span class="t-institution-payment-status">{{ payment.institutionPaymentStatus }}</span>
      </one-data-column>

      <one-data-column>
        <data-label slot="label">Payment ISO status</data-label>
        <span class="t-institution-iso-status">{{ payment.institutionIsoStatus }}</span>
      </one-data-column>
    </template>

    <one-data-column>
      <data-heading
        slot="label"
        heading="Application"
        spaced
      />
    </one-data-column>

    <one-data-column>
      <data-label slot="label">ID</data-label>
      <span class="t-application-id">{{ payment.applicationId }}</span>
    </one-data-column>

    <one-data-column>
      <data-label slot="label">Name</data-label>
      <span class="t-application-name">{{ getCountryName(payment.applicationName) }}</span>
    </one-data-column>

    <one-data-column>
      <data-label slot="label">Return URL</data-label>
      <span
        class="t-return-url"
        style="word-break: break-word"
      >{{ payment.applicationReturnUrl }}</span>
    </one-data-column>
  </div>
</template>

<script>
  import { createPaymentViewRoute } from '@/utils/routing/Payments'

  import OpenBankingPayment from '@/models/OpenBankingPayment'
  import countries from '@/mixins/countries'

  import DataLabel from '@/components/ui/Data/DataLabel'
  import OneDataColumn from '@/components/ui/Data/OneDataColumn'
  import OpenBankingPaymentStatusLabel from '@/components/open-banking/OpenBankingPaymentStatusLabel'
  import DataHeading from '@/components/ui/Data/DataHeading'

  export default {
    name: 'OpenBankingPaymentDetails',
    components: { DataHeading, OpenBankingPaymentStatusLabel, OneDataColumn, DataLabel },
    mixins: [countries],
    props: {
      payment: {
        type: OpenBankingPayment,
        required: true
      }
    },
    methods: {
      createPaymentViewRoute
    }
  }
</script>

<style lang="scss">

</style>
